import React, { useState, useEffect } from 'react';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  useToaster,
  Pagination,
  Modal,
  Message,
  IconButton,
  Whisper,
  Tooltip,
  ButtonToolbar
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import PlusIcon from '@rsuite/icons/Plus';
import { MdFilterList, MdDelete } from 'react-icons/md';
import { Edit, Icon } from '@rsuite/icons';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import FilterDrawer from './FilterDrawer';
import AddHolidayDrawer from './AddHolidayDrawer';
import EditHolidayDrawer from './EditDrawer';
import { fetchHolidays } from '@/services/feature.service';
import { deleteSetting  } from '@/services/feature.service';
import { useTranslation } from 'react-i18next';



const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const getTypeColor = (type: string | undefined) => {
  switch (type?.toLowerCase()) {
    case 'paid':
      return 'green';
    case 'unpaid':
      return 'orange';
    case 'religious':
      return 'blue';
    case 'national':
      return 'red';
    default:
      return 'gray';
  }
};

const HolidaysDataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();

  const { holidays, loading } = useSelector(state => state.feature);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showAddHolidayDrawer, setShowAddHolidayDrawer] = useState(false);
  const [showEditHolidayDrawer, setShowEditHolidayDrawer] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    islamic: '',
    recurrent: ''
  });
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchHolidays());
      // Log the holidays data after fetching
      console.log('Fetched Holidays:', holidays);
    };
    fetchData();
  }, [dispatch]);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const filteredData = holidays.filter(item => {
    if (filters.type && item.type?.toLowerCase() !== filters.type.toLowerCase()) {
      return false;
    }
  
    if (filters.islamic !== '' && item.islamic !== (filters.islamic === 'true')) {
      return false;
    }
  
    if (filters.recurrent !== '' && item.reccurent !== (filters.recurrent === 'true')) {
      return false;
    }
  
    if (searchKeyword) {
      const searchLower = searchKeyword.toLowerCase();
      const label = item.label?.toLowerCase() || '';
      const type = item.type?.toLowerCase() || '';
  
      return label.includes(searchLower) || type.includes(searchLower);
    }
  
    return true;
  });

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      type: '',
      islamic: '',
      recurrent: ''
    });
    setPage(1);
  };

  const paginatedData = filteredData.filter((v, i) => {
    const start = limit * (page - 1);
    const end = start + limit;
    return i >= start && i < end;
  });

  // Add console log to see the data being passed to the table
  console.log('Paginated Data:', paginatedData);

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleDelete = rowData => {
    setSelectedHoliday(rowData);
    setShowDeleteModal(true);
  };

  const handleEdit = rowData => {
    setSelectedHoliday(rowData);
    setShowEditHolidayDrawer(true);
  };
  const { t } = useTranslation();

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteSetting(selectedHoliday._id));

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Holiday deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);

      dispatch(fetchHolidays());
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting holiday')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setShowDeleteModal(false);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <InputGroup inside>
          <Input placeholder={t("Search")} value={searchKeyword} onChange={handleSearch} />
          <InputGroup.Addon>
            <SearchIcon />
          </InputGroup.Addon>
        </InputGroup>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowFilterDrawer(true)}>
            <MdFilterList style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={() => setShowAddHolidayDrawer(true)}>
            <PlusIcon style={{ marginRight: 8 }} /> {t('Add Holiday')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={paginatedData}
          loading={loading}
          rowHeight={60}
          wordWrap="break-word"
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('Holiday Name')}</HeaderCell>
            <Cell>{rowData => rowData.label}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Type')}</HeaderCell>
            <Cell>
              {rowData => (
                <Tag color={getTypeColor (rowData.type)}>{(t(rowData.type?.toUpperCase()))}</Tag>
              )}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Start Date')}</HeaderCell>
            <Cell>
              {rowData =>
                rowData.date_debut ? new Date(rowData.date_debut).toLocaleDateString() : 'Not set'
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('End Date')}</HeaderCell>
            <Cell>
              {rowData =>
                rowData.date_fin ? new Date(rowData.date_fin).toLocaleDateString() : 'Not set'
              }
            </Cell>
          </Column>
          <Column flexGrow={2}>
            <HeaderCell>{t('Islamic')}</HeaderCell>
            <Cell>{rowData => (rowData.islamic ? t('Yes') : t('No'))}</Cell>
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Recurrent')}</HeaderCell>
            <Cell>{rowData => (rowData.reccurent ? t('Yes') : t('No'))}</Cell>
          </Column>

          <Column width={100} flexGrow={0.5} fixed="right">
            <HeaderCell>{t('Actions')}</HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack spacing={6}>
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('Edit')}</Tooltip>}>
                    <IconButton
                      size="xs"
                      icon={<Icon as={Edit} />}
                      onClick={() => handleEdit(rowData)}
                      id={`edit-${rowData._id}`}
                    />
                  </Whisper>
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('Delete')}</Tooltip>}>
                    <IconButton
                      size="xs"
                      icon={<Icon as={MdDelete} style={{ color: 'red' }} />}
                      onClick={() => handleDelete(rowData)}
                      id={`delete-${rowData._id}`}
                    />
                  </Whisper>
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <FilterDrawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        toaster={toaster}
      />

      <AddHolidayDrawer
        open={showAddHolidayDrawer}
        onClose={() => setShowAddHolidayDrawer(false)}
        toaster={toaster}
      />

      <EditHolidayDrawer
        open={showEditHolidayDrawer}
        onClose={() => setShowEditHolidayDrawer(false)}
        toaster={toaster}
        holidayData={selectedHoliday}
      />

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Holiday')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Are you sure you want to delete the holiday')}"{selectedHoliday?.label}"? {t('This action cannot be undone.')}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HolidaysDataTable;